/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { OidcService, ExtendedOAuthService } from '@irian-ro/auth-oidc';

const OIDC_ARTEFACT_QUERY_KEYS: readonly string[] = [
  'code',
  'state',
  'session_state',
  'access_token',
  'id_token',
];

const parseJwt = (token: string) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(window.atob(base64));
};

//const starting in uppercase in order to have the same API as with classes
export const StoreAuthenticationTokenInLocalStorageIfPresentGuard: CanMatchFn = () => {
  const router = inject(Router);
  const oidcService = inject(OidcService);
  const oauthService = inject(OAuthService) as ExtendedOAuthService;
  const queryParams = router.getCurrentNavigation()?.initialUrl.queryParams;

  if (queryParams && queryParams['token']) {
    // If an OIDC callback is in flight (any artefact present), the `?token=` is either a
    // re-entry attempt or an injection vector — strip it and let routing continue WITHOUT
    // calling storeExternalToken. The legitimate `?token=` legacy login path never carries
    // an OIDC `code`/`state` alongside it.
    let hasOidcArtefact = false;
    for (let i = 0; i < OIDC_ARTEFACT_QUERY_KEYS.length; i++) {
      if (queryParams[OIDC_ARTEFACT_QUERY_KEYS[i]] != null) {
        hasOidcArtefact = true;
        break;
      }
    }
    if (hasOidcArtefact) {
      const cleanTree = router.getCurrentNavigation()?.initialUrl;
      if (cleanTree) {
        delete cleanTree.queryParams['token'];
      }
      return cleanTree ?? true;
    }

    const token = queryParams['token'];
    const decodedJwt = parseJwt(token);
    const expiresIn = decodedJwt.exp - Math.floor(Date.now() / 1000);

    oidcService.init();
    oauthService.storeExternalToken(token, expiresIn, decodedJwt.scope || 'openid profile email api.read');

    const currentUrlTree = router.getCurrentNavigation()?.initialUrl;
    delete currentUrlTree?.queryParams['token'];
    return currentUrlTree ?? true;
  }

  return true;
};
